import React, { useEffect, useRef } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import {
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonUndo,
  MenuControlsContainer,
  MenuDivider,
  RichTextEditor,
  type RichTextEditorRef,
} from 'mui-tiptap';

interface Props {
  value: string;
  onChange: (html: string) => void;
}

// Extensions are static — declared module-level so mui-tiptap's `useEditor`
// doesn't see a new reference on every parent render and re-initialise.
const extensions = [
  StarterKit,
  Link.configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
  }),
  // Pairs with <LinkBubbleMenu/> below — provides the imperative
  // `editor.commands.openLinkBubbleMenu()` that the link toolbar button calls.
  LinkBubbleMenuHandler,
];

export const StepRichTextEditor = ({ value, onChange }: Props) => {
  const ref = useRef<RichTextEditorRef>(null);

  // mui-tiptap's `content` prop is initial-content only; subsequent value
  // changes don't propagate automatically. Sync via the editor command when
  // the prop diverges from the editor's current HTML — covers row reorder,
  // cancel-and-reopen cycles, and external state hydration.
  useEffect(() => {
    const editor = ref.current?.editor;
    if (!editor) return;
    if (editor.getHTML() === value) return;
    // `emitUpdate: false` suppresses onUpdate so we don't loop.
    editor.commands.setContent(value || '', { emitUpdate: false });
  }, [value]);

  return (
    <RichTextEditor
      ref={ref}
      extensions={extensions}
      content={value}
      editable
      onUpdate={({ editor }) => onChange(editor.getHTML())}
      renderControls={() => (
        <MenuControlsContainer>
          <MenuButtonBold />
          <MenuButtonItalic />
          <MenuButtonCode />
          <MenuDivider />
          <MenuButtonBulletedList />
          <MenuButtonOrderedList />
          <MenuDivider />
          <MenuButtonEditLink />
          <MenuDivider />
          <MenuButtonRemoveFormatting />
          <MenuDivider />
          <MenuButtonUndo />
          <MenuButtonRedo />
        </MenuControlsContainer>
      )}
    >
      {() => <LinkBubbleMenu />}
    </RichTextEditor>
  );
};
